import { useState } from 'react';
import { useCompany, useSession } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner } from '../components/ui';
import { dateTime, enumLabel } from '../lib/format';
import { roleAtLeast } from '../lib/roles';

const ROLES = ['VIEWER', 'AUDITOR', 'EMPLOYEE', 'INVESTOR', 'LEGAL', 'ADMIN', 'OWNER'];

// Must match GRANTABLE_BY_LEGAL in src/routes/v1/index.js. LEGAL can onboard a
// new hire or investor's portal access; anything wider (who else can manage
// the register or the company) stays ADMIN+.
const GRANTABLE_BY_LEGAL = new Set(['EMPLOYEE', 'INVESTOR']);

const ROLE_HELP = {
  VIEWER: 'Read the cap table totals and share classes.',
  AUDITOR: 'Everything a viewer sees, plus the full register and the audit log.',
  EMPLOYEE: 'Only their own holdings, grants and documents.',
  INVESTOR: 'Only their own holdings and documents.',
  LEGAL: 'Full register, can issue/transfer/cancel/exercise, and can grant employee or investor portal access.',
  ADMIN: 'Everything legal can do, plus wider access management and closing rounds.',
  OWNER: 'Full control. Only an owner can grant the owner role.',
};

export default function Members() {
  const { companyId, role } = useCompany();
  const { profile } = useSession();
  const [inviting, setInviting] = useState(false);
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState(null);

  // Members and outstanding invitations are two tables in the database but one
  // question for an administrator — "who can get into this company?" — so they
  // render as one list with a pending marker.
  const list = useAsync(
    () => Promise.all([api.members(companyId), api.invitations(companyId)]),
    [companyId]
  );
  const isOwner = role === 'OWNER';
  const isAdmin = roleAtLeast(role, 'ADMIN');

  const rows = list.data
    ? [
        ...list.data[0].map((m) => ({ ...m, kind: 'member', key: `m:${m.id}` })),
        ...list.data[1].map((i) => ({ ...i, kind: 'invitation', key: `i:${i.id}` })),
      ]
    : [];

  async function remove(row) {
    setError(null);
    try {
      if (row.kind === 'invitation') {
        await api.revokeInvitation(companyId, row.id);
        setFlash(`Withdrew the invitation for ${row.email}.`);
      } else {
        await api.removeMember(companyId, row.id);
        setFlash(`Removed ${row.email}.`);
      }
      list.reload();
    } catch (err) {
      setError(err);
    }
  }

  async function resend(row) {
    setError(null);
    try {
      const result = await api.resendInvitation(companyId, row.id);
      setFlash(
        result.delivery?.emailed
          ? `Invitation re-sent to ${row.email}.`
          : `Invitation renewed for ${row.email}, but the email could not be sent.`
      );
      list.reload();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Access</h1>
          <p>
            Roles are read from the database on every request, never from a cached token
            claim — so revoking access takes effect immediately rather than whenever a
            session happens to refresh.
          </p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setInviting(true)}>Grant access</Button>
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />
      <ErrorBanner error={error} />

      {list.loading ? <Spinner /> : list.error ? <ErrorBanner error={list.error} onRetry={list.reload} /> : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.key}
            columns={[
              {
                key: 'email', header: 'Person',
                render: (r) => (
                  <span className="primary-cell">
                    {r.kind === 'invitation' ? r.email : r.display_name || r.email}
                    {r.kind === 'member' && r.id === profile?.user?.id ? (
                      <Badge tone="info"> you</Badge>
                    ) : null}
                  </span>
                ),
              },
              {
                key: 'status', header: 'Status',
                render: (r) =>
                  r.kind === 'member' ? (
                    <Badge tone="good">active</Badge>
                  ) : r.expired ? (
                    <Badge tone="warning">expired</Badge>
                  ) : (
                    <Badge tone="neutral">invited</Badge>
                  ),
              },
              { key: 'role', header: 'Role', render: (r) => <Badge tone={r.role === 'OWNER' ? 'good' : 'neutral'}>{enumLabel(r.role)}</Badge> },
              { key: 'invited', header: 'Invited by', render: (r) => r.invited_by_email ?? '—' },
              {
                key: 'since', header: 'Since',
                render: (r) =>
                  r.kind === 'member'
                    ? dateTime(r.created_at)
                    : r.expired
                      ? `expired ${dateTime(r.expires_at)}`
                      : `invited ${dateTime(r.created_at)}`,
              },
              {
                key: 'actions', header: '',
                render: (r) => {
                  if (r.kind === 'member') {
                    // Removing a member is ADMIN+ regardless of their role —
                    // offboarding is a different action than onboarding, and
                    // out of scope for what LEGAL can do here. You also cannot
                    // remove yourself: an admin who did would be locked out of
                    // the company they were administering.
                    if (!isAdmin || r.id === profile?.user?.id) return null;
                    return (
                      <Button size="sm" variant="ghost" onClick={() => remove(r)}>Remove</Button>
                    );
                  }
                  // LEGAL may resend/withdraw only the invitations it could
                  // have created itself (EMPLOYEE/INVESTOR); anything wider
                  // needs ADMIN, matching the backend check.
                  if (!isAdmin && !GRANTABLE_BY_LEGAL.has(r.role)) return null;
                  return (
                    <span className="row-actions">
                      <Button size="sm" variant="ghost" onClick={() => resend(r)}>
                        {r.expired ? 'Renew' : 'Resend'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)}>Withdraw</Button>
                    </span>
                  );
                },
              },
            ]}
            rows={rows}
          />
        </Card>
      )}

      <Card title="What each role can do">
        <dl className="role-help">
          {ROLES.map((r) => (
            <div key={r}>
              <dt><Badge tone="neutral">{r.toLowerCase()}</Badge></dt>
              <dd>{ROLE_HELP[r]}</dd>
            </div>
          ))}
        </dl>
      </Card>

      {inviting ? (
        <GrantModal
          companyId={companyId}
          isOwner={isOwner}
          isAdmin={isAdmin}
          onClose={() => setInviting(false)}
          onSaved={(email, result) => {
            setInviting(false);
            if (!result?.pending) {
              setFlash(`Granted access to ${email}.`);
            } else if (result.delivery?.emailed) {
              setFlash(`Invited ${email}. They get access when they first sign in.`);
            } else {
              // Saying "invited" here would be a lie the admin acts on. The
              // grant is real; the email is not.
              setFlash(
                `Access is reserved for ${email}, but the invitation email could not be sent — tell them directly. They get access when they first sign in.`
              );
            }
            list.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function GrantModal({ companyId, isOwner, isAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({ email: '', role: isAdmin ? 'VIEWER' : 'EMPLOYEE' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // 200 = granted now, 202 = invited. The caller reports which, so the
      // confirmation cannot claim an email was sent when none was.
      const result = await api.upsertMember(companyId, form);
      onSaved(form.email, result);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Grant access" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Grant</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <Field label="Email" required
          hint="They do not need an account yet. If this address is new, we email an invitation and access activates the first time they sign in with it.">
          <Input type="email" value={form.email} onChange={set('email')} required autoFocus />
        </Field>
        <Field label="Role" hint={ROLE_HELP[form.role]}>
          <Select value={form.role} onChange={set('role')}>
            {ROLES
              .filter((r) => r !== 'OWNER' || isOwner)
              .filter((r) => isAdmin || GRANTABLE_BY_LEGAL.has(r))
              .map((r) => (
                <option key={r} value={r}>{r.toLowerCase()}</option>
              ))}
          </Select>
        </Field>
        {!isAdmin ? (
          <p className="muted" style={{ fontSize: 12 }}>
            Legal can grant employee or investor access. Broader roles need an admin.
          </p>
        ) : !isOwner ? (
          <p className="muted" style={{ fontSize: 12 }}>
            Only an owner can grant the owner role.
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
